import {
  forwardRef,
  useCallback,
  useEffect,
  useRef,
  useState,
  type ButtonHTMLAttributes,
  type ReactNode,
} from 'react';
import { formatTime, hueFrom } from '../../lib/format';
import { DRIVE_STREAM_PREFIX } from '../../lib/swBridge';

/* ------------------------------------------------------------------ *
 * Buttons
 * ------------------------------------------------------------------ */

type ButtonBase = ButtonHTMLAttributes<HTMLButtonElement>;

export interface ButtonProps extends ButtonBase {
  /** Holds the selected state — for toggles and current selections. */
  active?: boolean;
  size?: 'sm' | 'md' | 'lg';
  children: ReactNode;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { active = false, size = 'md', className = '', children, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type="button"
      data-active={active || undefined}
      className={`glass press btn btn--${size} ${className}`}
      {...rest}
    >
      {children}
    </button>
  );
});

export interface IconButtonProps extends ButtonBase {
  label: string;
  active?: boolean;
  size?: number;
  /** For controls sitting inside a pane that is already glass. */
  bare?: boolean;
  children: ReactNode;
}

export function IconButton({
  label,
  active = false,
  size = 46,
  bare = false,
  className = '',
  children,
  ...rest
}: IconButtonProps) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      data-active={active || undefined}
      style={{ width: size, height: size }}
      className={`${bare ? 'iconbtn--bare' : 'glass'} press iconbtn ${className}`}
      {...rest}
    >
      {children}
    </button>
  );
}

/** A small labelled pill — speed, sleep timer, chapter count. */
export function Chip({
  active = false,
  className = '',
  children,
  ...rest
}: ButtonProps) {
  return (
    <button
      type="button"
      data-active={active || undefined}
      className={`press chip ${className}`}
      {...rest}
    >
      {children}
    </button>
  );
}

/* ------------------------------------------------------------------ *
 * Play button with progress ring
 * ------------------------------------------------------------------ */

export function PlayButton({
  playing,
  progress,
  busy,
  onClick,
}: {
  playing: boolean;
  /** 0..1 through the current chapter. */
  progress: number;
  busy?: boolean;
  onClick: () => void;
}) {
  const R = 46;
  const C = 2 * Math.PI * R;
  const clamped = Number.isFinite(progress) ? Math.min(1, Math.max(0, progress)) : 0;

  return (
    <div className="playbtn" data-playing={playing}>
      <svg className="playbtn__ring" viewBox="0 0 104 104" aria-hidden="true">
        <circle className="playbtn__groove" cx="52" cy="52" r={R} />
        <circle
          className="playbtn__arc"
          cx="52"
          cy="52"
          r={R}
          strokeDasharray={C}
          strokeDashoffset={C * (1 - clamped)}
        />
      </svg>
      <button
        type="button"
        onClick={onClick}
        aria-label={playing ? 'Pause' : 'Play'}
        className="press playbtn__hit"
        data-busy={busy || undefined}
      >
        {/* Both marks are always mounted and cross-fade, so the button morphs
         *  rather than flickering between two icons. */}
        <PlayGlyph hidden={playing} />
        <PauseGlyph hidden={!playing} />
      </button>
    </div>
  );
}

const PlayGlyph = ({ hidden }: { hidden: boolean }) => (
  <svg
    className="playglyph"
    data-hidden={hidden}
    viewBox="0 0 24 24"
    width="34"
    height="34"
    aria-hidden="true"
  >
    <path
      d="M8.5 5.4a.6.6 0 0 1 .92-.5l8.6 6.1a.6.6 0 0 1 0 1l-8.6 6.1a.6.6 0 0 1-.92-.5Z"
      fill="currentColor"
    />
  </svg>
);

const PauseGlyph = ({ hidden }: { hidden: boolean }) => (
  <svg
    className="playglyph"
    data-hidden={hidden}
    viewBox="0 0 24 24"
    width="34"
    height="34"
    aria-hidden="true"
  >
    <rect x="6.6" y="4.6" width="4.2" height="14.8" rx="1.7" fill="currentColor" />
    <rect x="13.2" y="4.6" width="4.2" height="14.8" rx="1.7" fill="currentColor" />
  </svg>
);

/* ------------------------------------------------------------------ *
 * Seek bar
 * ------------------------------------------------------------------ */

export interface SeekbarProps {
  value: number;
  max: number;
  onSeek: (seconds: number) => void;
  label: string;
  disabled?: boolean;
}

/**
 * A scrub bar that behaves like a native range input — pointer drag, arrow
 * keys, Home/End — rendered as a thin track with a lit fill and a thumb.
 *
 * While you are dragging, the displayed value comes from local state rather
 * than the audio element, otherwise incoming `timeupdate` events fight the
 * thumb and it stutters under your finger.
 */
export function Seekbar({ value, max, onSeek, label, disabled = false }: SeekbarProps) {
  const trackRef = useRef<HTMLDivElement>(null);
  const [dragValue, setDragValue] = useState<number | null>(null);

  const shown = dragValue ?? value;
  const safeMax = max > 0 ? max : 0;
  const ratio = safeMax > 0 ? Math.min(1, Math.max(0, shown / safeMax)) : 0;

  const valueFromEvent = useCallback(
    (clientX: number): number => {
      const rect = trackRef.current?.getBoundingClientRect();
      if (!rect || rect.width === 0 || safeMax <= 0) return 0;
      const pct = (clientX - rect.left) / rect.width;
      return Math.min(safeMax, Math.max(0, pct * safeMax));
    },
    [safeMax],
  );

  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (disabled || safeMax <= 0) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    setDragValue(valueFromEvent(e.clientX));
  };

  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (dragValue === null) return;
    setDragValue(valueFromEvent(e.clientX));
  };

  const endDrag = (e: React.PointerEvent<HTMLDivElement>) => {
    if (dragValue === null) return;
    const final = valueFromEvent(e.clientX);
    setDragValue(null);
    onSeek(final);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (disabled || safeMax <= 0) return;
    const step =
      e.key === 'PageUp' || e.key === 'PageDown' ? 60
      : e.shiftKey ? 30
      : 5;
    let next: number | null = null;
    if (e.key === 'ArrowRight' || e.key === 'ArrowUp' || e.key === 'PageUp') next = value + step;
    else if (e.key === 'ArrowLeft' || e.key === 'ArrowDown' || e.key === 'PageDown')
      next = value - step;
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = safeMax;
    if (next === null) return;
    e.preventDefault();
    onSeek(Math.min(safeMax, Math.max(0, next)));
  };

  return (
    <div
      ref={trackRef}
      className="seekbar"
      role="slider"
      tabIndex={disabled ? -1 : 0}
      aria-label={label}
      aria-valuemin={0}
      aria-valuemax={Math.round(safeMax)}
      aria-valuenow={Math.round(shown)}
      aria-valuetext={`${formatTime(shown)} of ${formatTime(safeMax)}`}
      aria-disabled={disabled || undefined}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
      onKeyDown={onKeyDown}
      data-dragging={dragValue !== null || undefined}
    >
      <div className="seekbar__track">
        <div className="seekbar__fill" style={{ width: `${ratio * 100}%` }} />
      </div>
      <div className="seekbar__thumb" style={{ left: `${ratio * 100}%` }} />
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Cover
 * ------------------------------------------------------------------ */

export function Cover({
  title,
  url,
  className = '',
}: {
  title: string;
  url?: string | undefined;
  className?: string;
}) {
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    setFailed(false);
    setAttempt(0);
  }, [url]);

  // Drive images load through the service worker. On a first visit an image
  // can be requested before the worker has taken control, and fail; show the
  // fallback, and try once more as soon as the worker is in charge.
  const onError = () => {
    setFailed(true);
    const sw = navigator.serviceWorker;
    if (attempt === 0 && url?.includes(DRIVE_STREAM_PREFIX) && sw && !sw.controller) {
      sw.addEventListener(
        'controllerchange',
        () => {
          setAttempt(1);
          setFailed(false);
        },
        { once: true },
      );
    }
  };

  if (url && !failed) {
    return (
      <img
        key={attempt}
        src={url}
        alt=""
        loading="lazy"
        className={`cover ${className}`}
        onError={onError}
      />
    );
  }

  // Generated fallback: a duotone tile carrying the actual title, keyed off a
  // hash of it so the same book always gets the same colours. Setting the type
  // reads as a designed jacket; initials alone read as a missing image.
  const hue = hueFrom(title);
  return (
    <div
      className={`cover cover--generated ${className}`}
      style={{
        background: `linear-gradient(145deg,
          hsl(${hue} 52% 58%),
          hsl(${(hue + 46) % 360} 48% 40%))`,
      }}
      aria-hidden="true"
    >
      <span className="cover__rule" />
      <span className="cover__title">{title}</span>
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Misc
 * ------------------------------------------------------------------ */

export function Sheet({
  className = '',
  children,
  ...rest
}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={`tile sheet ${className}`} {...rest}>
      {children}
    </div>
  );
}

/** Bouncing bars marking the book that is currently loaded. Frozen rather than
 *  hidden when paused, so the card still shows where you are. */
export function Equalizer({ playing }: { playing: boolean }) {
  return (
    <span className="eq" data-paused={!playing} aria-hidden="true">
      <span className="eq__bar" />
      <span className="eq__bar" />
      <span className="eq__bar" />
      <span className="eq__bar" />
    </span>
  );
}

/** Thin progress line used on library tiles and the resume panel. */
export function ProgressLine({ ratio, className = '' }: { ratio: number; className?: string }) {
  const pct = Math.min(1, Math.max(0, Number.isFinite(ratio) ? ratio : 0)) * 100;
  return (
    <div className={`progline ${className}`}>
      <div className="progline__fill" style={{ width: `${pct}%` }} />
    </div>
  );
}
